/**
 * Settings → Tools and Usage (slice 2a part 2 §2–§4): the tabs, each tool's
 * words, and the meters, apart from the components so they're tested alone.
 * Integrations' words are `integrations-view.ts`'s.
 */
import type { ToolId, ToolState, Usage } from "./api";

export type Tab = "ai" | "tools" | "integrations" | "messaging" | "usage";

/**
 * The tabs a person sees: AI and Usage are the owner's (§2; slice 6a §6.5).
 * Integrations sits between Tools and Usage, and a member sees it read-only
 * (slice 3b spec §5.1). Messaging follows it, everyone's own (slice 2e spec §6.1).
 */
export function tabsFor(role: "owner" | "member"): { tab: Tab; label: string; href: string }[] {
  const all = [
    { tab: "ai" as const, label: "AI", href: "/settings" },
    { tab: "tools" as const, label: "Tools", href: "/settings/tools" },
    { tab: "integrations" as const, label: "Integrations", href: "/settings/integrations" },
    { tab: "messaging" as const, label: "Messaging", href: "/settings/messaging" },
    { tab: "usage" as const, label: "Usage", href: "/settings/usage" },
  ];
  if (role === "owner") return all;
  // A member's Settings starts at Tools.
  return all
    .filter((t) => t.tab !== "usage" && t.tab !== "ai")
    .map((t) => (t.tab === "tools" ? { ...t, href: "/settings" } : t));
}

/**
 * The pages Settings links to after its tabs, each at its own route: Routines
 * and Memory, everyone's; Activity (slice 2c) and People, the owner's. The
 * sidebar left them for here; on their pages Settings is the sidebar's active row.
 */
export function pagesFor(role: "owner" | "member"): { label: string; href: string }[] {
  const owner = role === "owner";
  return [
    { label: "Routines", href: "/routines" },
    ...(owner ? [{ label: "Activity", href: "/activity" }] : []),
    { label: "Memory", href: "/memory" },
    ...(owner ? [{ label: "People", href: "/people" }] : []),
  ];
}

/** The tab a route names, or null for one that isn't a tab (it goes back to the first). */
export function tabOf(segment: string | undefined, role: "owner" | "member"): Tab | null {
  if (!segment) return role === "owner" ? "ai" : "tools";
  return tabsFor(role).find((t) => t.tab === segment)?.tab ?? null;
}

/** Each tool's name and line, in the page's words (§3). */
export const TOOL_WORDS: Record<ToolId, { name: string; line: string }> = {
  search_web: { name: "Search the web", line: "Finds pages that answer a question." },
  read_page: { name: "Read a page", line: "Reads a public page so it can quote and link to it." },
  browser: { name: "Browser", line: "For pages that need clicking, a form or a sign-in." },
  files: {
    name: "Files and commands",
    line: "Works with the workspace's files and runs quick commands. Programs like Python run on its computer.",
  },
  tasks: { name: "Tasks", line: "Opens and tracks work: tasks, their stage and their timeline." },
  docs: { name: "Docs", line: "Writes, edits and finds the workspace's docs." },
  crew_files: {
    name: "Your crew's files",
    line: "Finds the files people sent, opens them, and saves files it makes.",
  },
};

/** A state's words and tone (§3.1). */
export function stateWords(state: ToolState): { text: string; tone: "moss" | "copper" | "red" } {
  if (state === "capped") return { text: "Used up for today · back at midnight", tone: "copper" };
  if (state === "unavailable") return { text: "Not working right now", tone: "red" };
  return { text: "Always on", tone: "moss" };
}

/** Today's web meter (§4.2). */
export function todayMeter(web: Usage["web"]) {
  const used = Math.min(web.today, web.limit);
  return {
    capped: web.today >= web.limit,
    percent: web.limit > 0 ? Math.round((used / web.limit) * 100) : 0,
    used: `${web.today.toLocaleString("en-US")} of ${web.limit.toLocaleString("en-US")} used`,
    left: `${Math.max(web.limit - web.today, 0).toLocaleString("en-US")} left`,
  };
}

/** This month's bars (§4.3): each tool's share of the month's total; none when it's 0. */
export function monthBars(month: Usage["month"]) {
  const rows = [
    { id: "search_web" as const, n: month.search_web },
    { id: "read_page" as const, n: month.read_page },
  ];
  const total = rows.reduce((sum, r) => sum + r.n, 0);
  if (total === 0) return null;
  return rows.map((r) => ({
    id: r.id,
    name: TOOL_WORDS[r.id].name,
    count: r.n.toLocaleString("en-US"),
    percent: Math.round((r.n / total) * 100),
  }));
}
